<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthForm } from '@/composables/useAuthForm'
import { resetPassword } from '@/api/auth'

const route = useRoute()
const router = useRouter()
const { password, confirmPassword, token, email, handleSubmit, errors, loading, showToast } = useAuthForm('resetPassword')

onMounted(() => {
  const queryToken = route.query.token as string
  const queryEmail = route.query.email as string

  if (queryToken) {
    token.value = queryToken
  } else {
    showToast('error', 'Missing Token', 'No reset token found.')
  }

  if (queryEmail) {
    email.value = queryEmail
  } else {
    showToast('error', 'Missing Email', 'Email address not found in the link.')
  }
})

const onSubmit = handleSubmit(async () => {
  loading.value = true
  if (password.value !== confirmPassword.value) {
    showToast('error', 'Passwords do not match', 'Please ensure your passwords match.')
    loading.value = false
    return
  }
  try {
    await resetPassword({
      newPassword: password.value,
      token: token.value,
      email: email.value
    })
    showToast('success', 'Password Reset', 'Your password has been reset successfully. You can now log in with your new password.')
    await router.push('/login')
  } catch (err: any) {
    showToast('error', 'Reset Failed', err?.response?.data?.message || 'Something went wrong')
  } finally {
    loading.value = false
  }
});
</script>

<template>
  <div class="flex h-screen">
    <div class="hidden md:flex w-1/2 bg-[#f8f8f8] items-center justify-center bg-image"></div>

    <div class="w-full md:w-1/2 flex flex-col justify-center items-center px-6 sm:px-10">
      <img src="@/assets/images/logo.png" alt="Techknital Logo" class="w-48 mb-12" />

      <div class="text-center mb-12">
        <h2 class="text-3xl font-semibold text-gray-800 mb-2">Reset Password</h2>
        <p class="text-gray-500">Create a new password for your account.</p>
      </div>

      <form @submit.prevent="onSubmit" class="w-full max-w-sm space-y-8">
        <div>
          <label for="email" class="block text-gray-700 font-medium mb-2">Email</label>
          <InputText
              id="email"
              v-model="email"
              placeholder="Enter your Email"
              class="w-full"
              :class="{ 'p-invalid': errors.email }"
              disabled
          />
          <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
        </div>

        <div>
          <label for="password" class="block text-gray-700 font-medium mb-2">New Password</label>
          <Password
              id="password"
              v-model="password"
              :feedback="false"
              placeholder="Enter your new password"
              class="w-full"
              :class="{ 'p-invalid': errors.password }"
              toggleMask />
          <small v-if="errors.password" class="text-red-500">{{ errors.password }}</small>
        </div>

        <div>
          <label for="confirmPassword" class="block text-gray-700 font-medium mb-2">Confirm Password</label>
          <Password
              id="confirmPassword"
              v-model="confirmPassword"
              :feedback="false"
              placeholder="Confirm your new password"
              class="w-full"
              :class="{ 'p-invalid': errors.confirmPassword }"
              toggleMask />
          <small v-if="errors.confirmPassword" class="text-red-500">{{ errors.confirmPassword }}</small>
        </div>

        <Button type="submit" label="Reset Password" class="w-full bg-primary-500 border-none text-white text-lg py-3 rounded" :loading="loading" />
      </form>
    </div>
  </div>
</template>

<style scoped>
.p-password input {
  width: 100%;
}
.bg-image{
  background-image: url('@/assets/images/login_image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f8f8;
}
</style>
